import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import { useRex, useRexScreen } from '../../context/RexContext';
import { rexApplyCharter, AiUnavailableError } from '../../lib/ai';
import { downloadCharterWord } from '../../lib/charterWordExport';
import Loader from '../../components/Loader';
import InitiationLayout from '../../components/InitiationLayout';
import Badge from '../../components/Badge';
import {
  CHARTER_SECTION_LABELS,
  CHARTER_SECTION_ORDER,
  CHARTER_SECTION_FIELDS,
  CHARTER_STATUS_LABEL,
  CHARTER_SCOPE,
  charterScopeTitle,
  defaultCharterTitle,
  defaultCharterSectionContent,
  statusBadgeClass,
} from '../../lib/initiation';

const CURRENCIES = ['GBP', 'EUR', 'USD'];
const AUTO_SAVE_MS = 700;

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0] || '?').slice(0, 2).toUpperCase();
}

function isEmptyValue(value) {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function valuesDiffer(a, b) {
  return JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);
}

const REX_DRAFT_MARKER = '[rex-drafted]';

function hasRexDraftMarker(rexPrompt) {
  return (rexPrompt || '').includes(REX_DRAFT_MARKER);
}

function withRexDraftMarker(rexPrompt) {
  if (hasRexDraftMarker(rexPrompt)) return rexPrompt;
  return rexPrompt ? `${REX_DRAFT_MARKER} ${rexPrompt}` : REX_DRAFT_MARKER;
}

function rexInputClass(rexFilledKeys, fieldKey, { multiline = false, select = false } = {}) {
  const base = select ? 'form-select' : (multiline ? 'form-textarea' : 'form-input');
  return `${base} charter-inline-input${rexFilledKeys[fieldKey] ? ' charter-rex-filled' : ''}${multiline ? ' charter-auto-textarea' : ''}`;
}

function autoTextareaHeight(el, minRows = 1) {
  if (!el) return;
  const style = window.getComputedStyle(el);
  const lineHeight = parseFloat(style.lineHeight) || 18;
  const padY = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
  const borderY = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  const minHeight = lineHeight * minRows + padY + borderY;

  el.style.overflow = 'hidden';
  el.style.height = '0';
  el.style.height = `${Math.max(el.scrollHeight, minHeight) + 2}px`;
}

function resizeAllCharterTextareas(container) {
  if (!container) return;
  container.querySelectorAll('textarea.charter-auto-textarea').forEach((el) => {
    const minRows = parseInt(el.getAttribute('data-min-rows') || '1', 10);
    autoTextareaHeight(el, minRows);
  });
}

function AutoSizeTextarea({
  className, value, minRows = 2, onChange, onBlur, placeholder, ...props
}) {
  const ref = useRef(null);

  const resize = useCallback(() => {
    autoTextareaHeight(ref.current, minRows);
  }, [minRows]);

  useLayoutEffect(() => {
    resize();
    const raf = requestAnimationFrame(() => {
      resize();
      requestAnimationFrame(resize);
    });
    return () => cancelAnimationFrame(raf);
  }, [value, className, minRows, resize]);

  return (
    <textarea
      ref={ref}
      className={className?.includes('charter-auto-textarea') ? className : `${className || ''} charter-auto-textarea`.trim()}
      data-min-rows={minRows}
      rows={1}
      value={value}
      placeholder={placeholder}
      onChange={(e) => {
        onChange?.(e);
        autoTextareaHeight(e.target, minRows);
      }}
      onBlur={onBlur}
      {...props}
    />
  );
}

function charterTextareaClass(className) {
  const base = (className || 'form-textarea charter-inline-input')
    .replace(/\bform-input\b/, 'form-textarea');
  return base.includes('charter-auto-textarea') ? base : `${base} charter-auto-textarea`;
}

function CharterTextField({
  className, value, minRows = 1, multiline = false, type, onChange, onBlur, placeholder, ...props
}) {
  if (type === 'number') {
    return (
      <input
        className={className}
        type="number"
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        {...props}
      />
    );
  }

  return (
    <AutoSizeTextarea
      className={charterTextareaClass(className)}
      value={value ?? ''}
      minRows={multiline ? Math.max(minRows, 2) : 1}
      placeholder={placeholder}
      onChange={onChange}
      onBlur={onBlur}
      {...props}
    />
  );
}

function diffHeaderRexKeys(before, headerPatch) {
  const keys = {};
  ['title', 'summary', 'budget_envelope'].forEach((k) => {
    const nextVal = headerPatch?.[k];
    if (nextVal !== undefined && valuesDiffer(before?.[k], nextVal) && !isEmptyValue(nextVal)) {
      keys[`header.${k}`] = true;
    }
  });
  return keys;
}

function diffSectionRexKeys(sectionKey, before, after) {
  const keys = {};
  if (sectionKey === 'stakeholders') {
    if (valuesDiffer(before?.stakeholders, after?.stakeholders) && (after?.stakeholders || []).length) {
      keys['stakeholders.stakeholders'] = true;
    }
    return keys;
  }
  if (sectionKey === 'constraints_assumptions') {
    if (valuesDiffer(before?.constraints, after?.constraints) && (after?.constraints || []).length) {
      keys['constraints_assumptions.constraints'] = true;
    }
    if (valuesDiffer(before?.key_assumptions, after?.key_assumptions) && after?.key_assumptions) {
      keys['constraints_assumptions.key_assumptions'] = true;
    }
    return keys;
  }
  (CHARTER_SECTION_FIELDS[sectionKey] || []).forEach((f) => {
    if (valuesDiffer(before?.[f.key], after?.[f.key]) && !isEmptyValue(after?.[f.key])) {
      keys[`${sectionKey}.${f.key}`] = true;
    }
  });
  return keys;
}

function FieldSkeleton({ lines = 1 }) {
  return (
    <div className="field-skeleton-wrap" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className={`field-skeleton${i === lines - 1 && lines > 1 ? ' medium' : ''}${lines === 1 ? ' short' : ''}`}
        />
      ))}
    </div>
  );
}

function FieldDisplay({ label, value }) {
  const empty = isEmptyValue(value);
  return (
    <div className="charter-field">
      <div className="charter-field-label">{label}</div>
      <div className={`charter-field-value${empty ? ' placeholder' : ''}`}>
        {empty ? 'Not set' : value}
      </div>
    </div>
  );
}

function SectionFieldsForm({
  sectionKey, content, onChange, onBlur, readOnly, rexFilledKeys, onClearRexField, showSkeleton,
}) {
  const clearRex = (fieldKey) => () => onClearRexField?.(fieldKey);

  if (showSkeleton) {
    if (sectionKey === 'stakeholders') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1].map((i) => (
            <div key={i} className="form-2col" style={{ alignItems: 'end' }}>
              <div className="form-row">
                <label className="form-label">Name</label>
                <FieldSkeleton lines={1} />
              </div>
              <div className="form-row">
                <label className="form-label">Role</label>
                <FieldSkeleton lines={1} />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (sectionKey === 'constraints_assumptions') {
      return (
        <>
          <div className="form-row">
            <label className="form-label">Constraints (one per line)</label>
            <FieldSkeleton lines={3} />
          </div>
          <div className="form-row">
            <label className="form-label">Key assumptions</label>
            <FieldSkeleton lines={3} />
          </div>
        </>
      );
    }
    const fields = CHARTER_SECTION_FIELDS[sectionKey] || [];
    return (
      <div className="charter-field-row charter-field-edit">
        {fields.map((f) => (
          <div key={f.key} className="form-row">
            <label className="form-label">{f.label}</label>
            <FieldSkeleton lines={f.multiline ? 3 : 1} />
          </div>
        ))}
      </div>
    );
  }

  if (sectionKey === 'stakeholders') {
    const list = content.stakeholders || [];
    if (readOnly) {
      return (
        <div className="stakeholder-list">
          {list.length === 0 ? (
            <span className="charter-field-value placeholder">No stakeholders added</span>
          ) : (
            list.map((s, i) => (
              <div key={i} className="stakeholder-pill">
                <div className="stakeholder-avatar">{initials(s.name)}</div>
                <div>
                  <div>{s.name}</div>
                  <div className="stakeholder-role">{s.role}</div>
                </div>
              </div>
            ))
          )}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map((s, i) => (
          <div key={i} className="form-2col" style={{ alignItems: 'end' }}>
            <div className="form-row">
              <label className="form-label">Name</label>
              <CharterTextField
                className={rexInputClass(rexFilledKeys, 'stakeholders.stakeholders', { multiline: true })}
                value={s.name || ''}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = { ...next[i], name: e.target.value };
                  onChange({ ...content, stakeholders: next });
                }}
                onBlur={() => { onBlur?.(); clearRex('stakeholders.stakeholders')(); }}
              />
            </div>
            <div className="form-row">
              <label className="form-label">Role</label>
              <CharterTextField
                className={rexInputClass(rexFilledKeys, 'stakeholders.stakeholders', { multiline: true })}
                value={s.role || ''}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = { ...next[i], role: e.target.value };
                  onChange({ ...content, stakeholders: next });
                }}
                onBlur={() => { onBlur?.(); clearRex('stakeholders.stakeholders')(); }}
              />
            </div>
            <button
              type="button"
              className="row-act row-act-close"
              onClick={() => onChange({ ...content, stakeholders: list.filter((_, j) => j !== i) })}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-ghost"
          onClick={() => onChange({ ...content, stakeholders: [...list, { name: '', role: '' }] })}
        >
          + Add stakeholder
        </button>
      </div>
    );
  }

  const fields = CHARTER_SECTION_FIELDS[sectionKey] || [];
  if (readOnly) {
    if (sectionKey === 'constraints_assumptions') {
      const tags = content.constraints || [];
      return (
        <>
          <div className="charter-field">
            <div className="charter-field-label">Constraints</div>
            {tags.length ? (
              <div className="tag-list">
                {tags.map((t, i) => (
                  <span key={i} className={`tag${t.kind === 'risk' ? ' risk' : ' info'}`}>{t.text || t}</span>
                ))}
              </div>
            ) : (
              <div className="charter-field-value placeholder">Not set</div>
            )}
          </div>
          <FieldDisplay label="Key assumptions" value={content.key_assumptions} />
        </>
      );
    }
    const cols = sectionKey === 'timeline_budget' || sectionKey === 'success_criteria' ? 3 : 2;
    return (
      <div className={`charter-field-row${cols === 3 ? ' three' : ''}`}>
        {fields.map((f) => (
          <FieldDisplay key={f.key} label={f.label} value={content[f.key]} />
        ))}
      </div>
    );
  }

  if (sectionKey === 'constraints_assumptions') {
    const text = (content.constraints || []).map((c) => (typeof c === 'string' ? c : c.text)).join('\n');
    return (
      <>
        <div className="form-row">
          <label className="form-label">Constraints (one per line)</label>
          <AutoSizeTextarea
            className={rexInputClass(rexFilledKeys, 'constraints_assumptions.constraints', { multiline: true })}
            minRows={3}
            value={text}
            onChange={(e) => {
              const lines = e.target.value.split('\n').map((l) => l.trim()).filter(Boolean);
              onChange({
                ...content,
                constraints: lines.map((line) => ({ text: line, kind: 'info' })),
              });
            }}
            onBlur={() => { onBlur?.(); clearRex('constraints_assumptions.constraints')(); }}
          />
        </div>
        <div className="form-row">
          <label className="form-label">Key assumptions</label>
          <AutoSizeTextarea
            className={rexInputClass(rexFilledKeys, 'constraints_assumptions.key_assumptions', { multiline: true })}
            minRows={2}
            value={content.key_assumptions || ''}
            onChange={(e) => onChange({ ...content, key_assumptions: e.target.value })}
            onBlur={() => { onBlur?.(); clearRex('constraints_assumptions.key_assumptions')(); }}
          />
        </div>
      </>
    );
  }

  return (
    <div className="charter-field-row charter-field-edit">
      {fields.map((f) => {
        const fieldKey = `${sectionKey}.${f.key}`;
        return (
          <div key={f.key} className="form-row">
            <label className="form-label">{f.label}</label>
            <CharterTextField
              className={rexInputClass(rexFilledKeys, fieldKey, { multiline: f.multiline })}
              multiline={f.multiline}
              minRows={f.lines ? 4 : 2}
              value={content[f.key] || ''}
              placeholder="Not set"
              onChange={(e) => onChange({ ...content, [f.key]: e.target.value })}
              onBlur={() => { onBlur?.(); clearRex(fieldKey)(); }}
            />
          </div>
        );
      })}
    </div>
  );
}

function CharterSectionCard({
  section, readOnly, onSave, saveStatus, rexFilledKeys, onClearRexField, showSkeleton,
}) {
  const [draft, setDraft] = useState(section.content_json || {});
  const saveTimer = useRef(null);
  const draftRef = useRef(draft);
  const bodyRef = useRef(null);
  draftRef.current = draft;

  useEffect(() => {
    setDraft(section.content_json || defaultCharterSectionContent(section.section_key));
  }, [section]);

  useLayoutEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    const run = () => {
      if (!cancelled) resizeAllCharterTextareas(bodyRef.current);
    };
    run();
    const t = window.setTimeout(run, 0);
    const raf = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      cancelAnimationFrame(raf);
    };
  }, [draft, readOnly, section.id, section.content_json]);

  const flushSave = useCallback(async (silent = true) => {
    await onSave(section.id, draftRef.current, { silent });
  }, [onSave, section.id]);

  const scheduleSave = useCallback((next) => {
    setDraft(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onSave(section.id, next, { silent: true });
    }, AUTO_SAVE_MS);
  }, [onSave, section.id]);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  const handleBlur = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    flushSave(true);
  };

  return (
    <div className="charter-section">
      <div className="charter-section-header">
        <div className="charter-section-title">
          {section.title || CHARTER_SECTION_LABELS[section.section_key]}
        </div>
        {!readOnly && saveStatus === 'saving' && (
          <span className="charter-save-hint">Saving…</span>
        )}
      </div>
      <div className="charter-section-body" ref={bodyRef}>
        <SectionFieldsForm
          sectionKey={section.section_key}
          content={draft}
          onChange={scheduleSave}
          onBlur={handleBlur}
          readOnly={readOnly}
          rexFilledKeys={rexFilledKeys}
          onClearRexField={onClearRexField}
          showSkeleton={showSkeleton}
        />
      </div>
    </div>
  );
}

export default function Charter() {
  const { programme, project } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();
  const { messages, loading: rexLoading, requestOpenPanel } = useRex();
  const [searchParams, setSearchParams] = useSearchParams();
  const canSwitchScope = Boolean(project?.id);
  const charterScope = canSwitchScope && searchParams.get('scope') === CHARTER_SCOPE.programme
    ? CHARTER_SCOPE.programme
    : (canSwitchScope ? CHARTER_SCOPE.project : CHARTER_SCOPE.programme);

  const scopeTitle = charterScopeTitle(charterScope);
  const scopeEntityName = charterScope === CHARTER_SCOPE.project
    ? (project?.name || 'Project')
    : (programme?.name || 'Programme');

  const [charter, setCharter] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectionSaveStatus, setSectionSaveStatus] = useState('idle');
  const [toast, setToast] = useState(null);
  const [headerDraft, setHeaderDraft] = useState({ title: '', summary: '', budget_envelope: '', budget_currency: 'GBP' });
  const [rexFilledKeys, setRexFilledKeys] = useState({});
  const [rexEditedByRex, setRexEditedByRex] = useState(false);
  const [rexApplying, setRexApplying] = useState(false);
  const [showRexBanner, setShowRexBanner] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const headerSaveTimer = useRef(null);
  const headerDraftRef = useRef(headerDraft);
  headerDraftRef.current = headerDraft;
  const lastRexAssistant = useRef(0);
  const charterBodyRef = useRef(null);
  const locked = charter?.status === 'review' || charter?.status === 'approved';
  const readOnly = !canWrite || locked;

  useLayoutEffect(() => {
    if (!charter || locked) return undefined;
    let cancelled = false;
    const run = () => {
      if (!cancelled) resizeAllCharterTextareas(charterBodyRef.current);
    };
    run();
    const t = window.setTimeout(run, 0);
    const t2 = window.setTimeout(run, 100);
    const raf = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      window.clearTimeout(t2);
      cancelAnimationFrame(raf);
    };
  }, [charter, locked, sections, headerDraft, rexApplying, rexFilledKeys]);

  const showToast = useCallback((msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 8000 : 4000);
  }, []);

  const rexSnapshot = useMemo(() => ({
    charterScope,
    charterStatus: charter?.status || 'draft',
    sectionCount: sections.length,
    locked,
    programmeName: programme?.name,
    projectName: project?.name || null,
    header: charter ? {
      title: charter.title,
      summary: charter.summary,
      budget_envelope: charter.budget_envelope,
      budget_currency: charter.budget_currency,
    } : null,
    sections: sections.map((s) => ({
      key: s.section_key,
      title: s.title || CHARTER_SECTION_LABELS[s.section_key],
      content: s.content_json,
    })),
  }), [charter, sections, locked, programme?.name, project?.name, charterScope]);

  const clearRexField = useCallback((fieldKey) => {
    setRexFilledKeys((prev) => {
      if (!prev[fieldKey]) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  }, []);

  const ensureCharterRow = useCallback(async () => {
    if (!programme?.id) throw new Error('Open a programme to create a charter');
    if (charterScope === CHARTER_SCOPE.project && !project?.id) {
      throw new Error('Open a project to create a project charter');
    }
    if (charter?.id) return { charter, sections };

    const { data, error } = await sb
      .from('programme_charters')
      .insert({
        programme_id: programme.id,
        project_id: charterScope === CHARTER_SCOPE.project ? project.id : null,
        status: 'draft',
        title: defaultCharterTitle(charterScope, { programmeName: programme.name, projectName: project?.name }),
        created_by: profile?.id || null,
      })
      .select()
      .single();
    if (error) throw error;

    const sectionRows = CHARTER_SECTION_ORDER.map((key, i) => ({
      charter_id: data.id,
      section_key: key,
      title: CHARTER_SECTION_LABELS[key],
      order_index: i,
      content_json: defaultCharterSectionContent(key),
    }));
    const { data: secs, error: secErr } = await sb.from('charter_sections').insert(sectionRows).select();
    if (secErr) throw secErr;

    setCharter(data);
    setSections(secs || []);
    setHeaderDraft({
      title: data.title || defaultCharterTitle(charterScope, { programmeName: programme.name, projectName: project?.name }) || '',
      summary: '',
      budget_envelope: '',
      budget_currency: 'GBP',
    });

    return { charter: data, sections: secs || [] };
  }, [charter, sections, programme?.id, programme?.name, project?.id, project?.name, charterScope, profile?.id]);

  const applyRexFill = useCallback(async (proposalText, { quiet = false } = {}) => {
    if (readOnly || !proposalText?.trim()) return false;
    setRexApplying(true);
    try {
      let activeCharter = charter;
      let activeSections = sections;
      if (!activeCharter?.id) {
        const ensured = await ensureCharterRow();
        activeCharter = ensured.charter;
        activeSections = ensured.sections;
      }

      const parsed = await rexApplyCharter({
        proposal: proposalText,
        header: {
          title: activeCharter.title || programme?.name || '',
          summary: activeCharter.summary || '',
          budget_envelope: activeCharter.budget_envelope,
        },
        sections: Object.fromEntries(
          activeSections.map((s) => [s.section_key, s.content_json || defaultCharterSectionContent(s.section_key)])
        ),
      });

      const { header, sections: sectionUpdates } = parsed;
      const newRexKeys = {};

      if (header && Object.keys(header).length) {
        const nextHeader = {
          title: header.title ?? activeCharter.title ?? programme?.name ?? '',
          summary: header.summary ?? activeCharter.summary ?? '',
          budget_envelope: header.budget_envelope ?? activeCharter.budget_envelope,
          budget_currency: activeCharter.budget_currency || 'GBP',
        };
        Object.assign(
          newRexKeys,
          diffHeaderRexKeys(
            { title: activeCharter.title, summary: activeCharter.summary, budget_envelope: activeCharter.budget_envelope },
            nextHeader
          )
        );
        setHeaderDraft({
          title: nextHeader.title || '',
          summary: nextHeader.summary || '',
          budget_envelope: nextHeader.budget_envelope != null ? String(nextHeader.budget_envelope) : '',
          budget_currency: nextHeader.budget_currency,
        });
        const budget = String(nextHeader.budget_envelope ?? '').trim();
        const { error } = await sb
          .from('programme_charters')
          .update({
            title: nextHeader.title?.trim() || null,
            summary: nextHeader.summary?.trim() || null,
            budget_envelope: budget ? Number(budget) : null,
            updated_by: profile?.id || null,
            status: activeCharter.status === 'empty' ? 'draft' : activeCharter.status,
            rex_prompt: withRexDraftMarker(activeCharter.rex_prompt),
          })
          .eq('id', activeCharter.id);
        if (error) throw error;
        activeCharter = {
          ...activeCharter,
          title: nextHeader.title,
          summary: nextHeader.summary,
          budget_envelope: budget ? Number(budget) : null,
          status: activeCharter.status === 'empty' ? 'draft' : activeCharter.status,
          rex_prompt: withRexDraftMarker(activeCharter.rex_prompt),
        };
        setCharter(activeCharter);
      }

      for (const [sectionKey, contentJson] of Object.entries(sectionUpdates || {})) {
        const section = activeSections.find((s) => s.section_key === sectionKey);
        if (!section || !contentJson) continue;
        const before = section.content_json || defaultCharterSectionContent(sectionKey);
        const merged = { ...before, ...contentJson };
        Object.assign(newRexKeys, diffSectionRexKeys(sectionKey, before, merged));
        const { error } = await sb
          .from('charter_sections')
          .update({
            content_json: merged,
            updated_by: profile?.id || null,
          })
          .eq('id', section.id);
        if (error) throw error;
        setSections((prev) =>
          prev.map((s) => (s.id === section.id ? { ...s, content_json: merged } : s))
        );
      }

      if (activeCharter.status === 'empty') {
        await sb.from('programme_charters').update({ status: 'draft' }).eq('id', activeCharter.id);
        activeCharter = { ...activeCharter, status: 'draft' };
        setCharter(activeCharter);
      }

      if (!Object.keys(newRexKeys).length) {
        if (!quiet) {
          showToast('Rex has not drafted charter sections yet — try describing the programme in more detail.');
        }
        return false;
      }

      setRexFilledKeys((prev) => ({ ...prev, ...newRexKeys }));
      setRexEditedByRex(true);
      setShowRexBanner(true);
      return true;
    } catch (e) {
      const raw = e instanceof AiUnavailableError
        ? e.message
        : (e?.message || 'Could not apply Rex draft');
      const noStructuredContent = /no charter updates found/i.test(raw);
      if (quiet && noStructuredContent) return false;
      if (noStructuredContent) {
        showToast('Rex has not drafted charter sections yet — try describing the programme in more detail.');
        return false;
      }
      showToast(raw);
      return false;
    } finally {
      setRexApplying(false);
    }
  }, [readOnly, charter, programme?.name, sections, profile?.id, showToast, ensureCharterRow]);

  const rexSuggestions = useMemo(() => {
    const scopeHint = charterScope === CHARTER_SCOPE.project ? 'project' : 'programme';
    if (!charter) return [`Draft the ${scopeHint} charter from a short brief`, 'What belongs in each section?'];
    if (locked && charter.status === 'review') return ['Charter is in review — use Recall to edit'];
    if (locked) return ['Charter is approved — proceed to requirements or process scope'];
    if (rexEditedByRex) return ['Shorten the summary', 'Add more stakeholders', 'Tighten success criteria'];
    return [`Draft all ${scopeHint} charter sections from a short brief`, 'What sections are still empty?', 'Suggest success criteria'];
  }, [charter, locked, rexEditedByRex, charterScope]);

  const rexPriming = useMemo(() => {
    if (!charter || readOnly) return null;
    if (rexEditedByRex) return "Charter's drafted. Want me to adjust anything?";
    return null;
  }, [charter, readOnly, rexEditedByRex]);

  const rexDrafting = rexLoading || rexApplying;

  useRexScreen({
    snapshot: rexSnapshot,
    suggestions: rexSuggestions,
    priming: rexPriming,
    charterDrafting: rexDrafting,
    charterDraftComplete: showRexBanner && !rexDrafting,
  });

  useEffect(() => {
    if (readOnly || rexLoading || rexApplying) return;
    const assistantCount = messages.filter((m) => m.role === 'assistant').length;
    if (assistantCount === 0 || assistantCount === lastRexAssistant.current) return;
    const last = messages[messages.length - 1];
    if (last?.role !== 'assistant') return;
    lastRexAssistant.current = assistantCount;
    applyRexFill(last.content, { quiet: true });
  }, [messages, rexLoading, rexApplying, readOnly, applyRexFill]);

  const load = useCallback(async () => {
    if (!programme?.id) return;
    setLoading(true);
    try {
      let query = sb
        .from('programme_charters')
        .select('*')
        .eq('programme_id', programme.id);

      if (charterScope === CHARTER_SCOPE.project && project?.id) {
        query = query.eq('project_id', project.id);
      } else {
        query = query.is('project_id', null);
      }

      const { data: row } = await query.maybeSingle();
      setCharter(row || null);
      if (row?.id) {
        const { data: secs } = await sb
          .from('charter_sections')
          .select('*')
          .eq('charter_id', row.id)
          .order('order_index');
        setSections(secs || []);
        setHeaderDraft({
          title: row.title || defaultCharterTitle(charterScope, { programmeName: programme.name, projectName: project?.name }) || '',
          summary: row.summary || '',
          budget_envelope: row.budget_envelope != null ? String(row.budget_envelope) : '',
          budget_currency: row.budget_currency || 'GBP',
        });
        setRexEditedByRex(hasRexDraftMarker(row.rex_prompt));
        setRexFilledKeys({});
        setShowRexBanner(false);
      } else {
        setSections([]);
        setHeaderDraft({
          title: defaultCharterTitle(charterScope, { programmeName: programme.name, projectName: project?.name }) || '',
          summary: '',
          budget_envelope: '',
          budget_currency: 'GBP',
        });
      }
    } catch (e) {
      console.error('[Charter] load failed', e);
      showToast('Failed to load charter');
    } finally {
      setLoading(false);
    }
  }, [programme?.id, programme?.name, project?.id, project?.name, charterScope, showToast]);

  useEffect(() => {
    setRexFilledKeys({});
    setShowRexBanner(false);
    lastRexAssistant.current = 0;
  }, [charterScope, programme?.id, project?.id]);

  useEffect(() => { load(); }, [load]);

  async function createCharter() {
    setSaving(true);
    try {
      await ensureCharterRow();
      showToast('Charter created with 5 sections', 'success');
    } catch (e) {
      showToast(e.message || 'Could not create charter');
    } finally {
      setSaving(false);
    }
  }

  const saveHeader = useCallback(async (draft, { silent } = {}) => {
    if (!charter?.id || readOnly) return;
    if (!silent) setSaving(true);
    try {
      const budget = draft.budget_envelope.trim();
      const { error } = await sb
        .from('programme_charters')
        .update({
          title: draft.title.trim() || null,
          summary: draft.summary.trim() || null,
          budget_envelope: budget ? Number(budget) : null,
          budget_currency: draft.budget_currency || null,
          updated_by: profile?.id || null,
          status: charter.status === 'empty' ? 'draft' : charter.status,
        })
        .eq('id', charter.id);
      if (error) throw error;
      setCharter((c) => ({
        ...c,
        title: draft.title,
        summary: draft.summary,
        budget_envelope: budget ? Number(budget) : null,
        budget_currency: draft.budget_currency,
        status: c.status === 'empty' ? 'draft' : c.status,
      }));
      if (!silent) showToast('Charter header saved', 'success');
    } catch (e) {
      showToast(e.message || 'Save failed');
    } finally {
      if (!silent) setSaving(false);
    }
  }, [charter, readOnly, profile?.id, showToast]);

  const scheduleHeaderSave = (patch) => {
    setHeaderDraft((prev) => {
      const next = { ...prev, ...patch };
      if (headerSaveTimer.current) clearTimeout(headerSaveTimer.current);
      headerSaveTimer.current = setTimeout(() => saveHeader(next, { silent: true }), AUTO_SAVE_MS);
      return next;
    });
  };

  const flushHeaderSave = () => {
    if (headerSaveTimer.current) clearTimeout(headerSaveTimer.current);
    saveHeader(headerDraftRef.current, { silent: true });
  };

  async function saveSection(sectionId, contentJson, { silent } = {}) {
    if (!charter?.id || readOnly) return;
    if (silent) setSectionSaveStatus('saving');
    else setSaving(true);
    try {
      const { error } = await sb
        .from('charter_sections')
        .update({
          content_json: contentJson,
          updated_by: profile?.id || null,
        })
        .eq('id', sectionId);
      if (error) throw error;

      if (contentJson.total_budget && charter.budget_envelope == null) {
        const num = parseFloat(String(contentJson.total_budget).replace(/[^0-9.]/g, ''));
        if (!Number.isNaN(num)) {
          await sb.from('programme_charters').update({ budget_envelope: num }).eq('id', charter.id);
          setCharter((c) => ({ ...c, budget_envelope: num }));
          setHeaderDraft((p) => ({ ...p, budget_envelope: String(num) }));
        }
      }

      setSections((prev) =>
        prev.map((s) => (s.id === sectionId ? { ...s, content_json: contentJson } : s))
      );
      if (charter.status === 'empty') {
        await sb.from('programme_charters').update({ status: 'draft' }).eq('id', charter.id);
        setCharter((c) => ({ ...c, status: 'draft' }));
      }
      if (!silent) showToast('Section saved', 'success');
    } catch (e) {
      showToast(e.message || 'Section save failed');
    } finally {
      if (silent) setSectionSaveStatus('idle');
      else setSaving(false);
    }
  }

  async function submitForReview() {
    if (!charter?.id || readOnly) return;
    setSaving(true);
    try {
      const { error } = await sb
        .from('programme_charters')
        .update({
          status: 'review',
          submitted_at: new Date().toISOString(),
          submitted_by: profile?.id || null,
        })
        .eq('id', charter.id);
      if (error) throw error;
      setCharter((c) => ({ ...c, status: 'review' }));
      setRexFilledKeys({});
      showToast('Submitted for approval', 'success');
    } catch (e) {
      showToast(e.message || 'Submit failed');
    } finally {
      setSaving(false);
    }
  }

  async function recallCharter() {
    if (!charter?.id || charter.status !== 'review') return;
    setSaving(true);
    try {
      const { error } = await sb
        .from('programme_charters')
        .update({
          status: 'draft',
          submitted_at: null,
          submitted_by: null,
        })
        .eq('id', charter.id);
      if (error) throw error;
      setCharter((c) => ({ ...c, status: 'draft', submitted_at: null, submitted_by: null }));
      showToast('Charter recalled to draft', 'success');
    } catch (e) {
      showToast(e.message || 'Recall failed');
    } finally {
      setSaving(false);
    }
  }

  async function resetCharter() {
    if (!charter?.id) return;
    if (locked) {
      showToast(charter.status === 'approved'
        ? 'Approved charters cannot be reset'
        : 'Recall to draft before resetting');
      return;
    }
    setSaving(true);
    try {
      const { error } = await sb.from('programme_charters').delete().eq('id', charter.id);
      if (error) throw error;
      setCharter(null);
      setSections([]);
      setRexFilledKeys({});
      setRexEditedByRex(false);
      setShowRexBanner(false);
      lastRexAssistant.current = 0;
      setHeaderDraft({
        title: defaultCharterTitle(charterScope, { programmeName: programme?.name, projectName: project?.name }) || '',
        summary: '',
        budget_envelope: '',
        budget_currency: 'GBP',
      });
      setShowResetModal(false);
      showToast(`${scopeTitle} reset — start again when ready`, 'success');
    } catch (e) {
      showToast(e.message || 'Reset failed');
    } finally {
      setSaving(false);
    }
  }

  function setCharterScope(nextScope) {
    if (nextScope === charterScope) return;
    const next = new URLSearchParams(searchParams);
    if (nextScope === CHARTER_SCOPE.programme) {
      next.set('scope', CHARTER_SCOPE.programme);
    } else {
      next.delete('scope');
    }
    setSearchParams(next, { replace: true });
  }

  async function approveCharter() {
    if (!charter?.id) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const { error } = await sb
        .from('programme_charters')
        .update({
          status: 'approved',
          approved_at: now,
          approved_by: profile?.id || null,
        })
        .eq('id', charter.id);
      if (error) throw error;

      if (profile?.id) {
        await sb.from('charter_approvals').upsert(
          {
            charter_id: charter.id,
            approver_id: profile.id,
            status: 'approved',
            decided_at: now,
          },
          { onConflict: 'charter_id,approver_id' }
        );
      }

      setCharter((c) => ({ ...c, status: 'approved', approved_at: now }));
      setRexFilledKeys({});
      showToast('Charter approved', 'success');
    } catch (e) {
      showToast(e.message || 'Approval failed');
    } finally {
      setSaving(false);
    }
  }

  function handleDownloadWord() {
    if (!charter) return;
    try {
      flushHeaderSave();
      const filename = downloadCharterWord({
        charter,
        header: headerDraftRef.current,
        sections,
        programmeName: programme?.name,
        projectName: project?.name,
        charterScope,
      });
      showToast(`Downloaded ${filename}`, 'success');
    } catch (e) {
      showToast(e.message || 'Could not export charter');
    }
  }

  if (loading) return <Loader text="Loading charter…" />;

  if (!programme?.id) {
    return (
      <InitiationLayout title={scopeTitle} subtitle="IN-01">
        <div className="empty">Open a programme or project to edit charters.</div>
      </InitiationLayout>
    );
  }

  const status = charter?.status || 'empty';
  const statusLabel = status === 'draft' && rexEditedByRex
    ? 'Draft · edited by Rex'
    : (CHARTER_STATUS_LABEL[status] || status);
  const badge = charter
    ? <span className={`b ${statusBadgeClass(status)}`}>{statusLabel}</span>
    : <span className="b">Not started</span>;

  const downloadWordButton = (
    <button type="button" className="btn-ghost" disabled={saving || rexApplying} onClick={handleDownloadWord}>
      Download Word
    </button>
  );

  const actions = !canWrite ? (charter ? downloadWordButton : null) : charter && charter.status === 'review' ? (
    <>
      {downloadWordButton}
      <button type="button" className="btn-ghost" style={{ color: '#A32D2D' }} disabled={saving} onClick={recallCharter}>
        Recall
      </button>
      <button type="button" className="btn-primary" disabled={saving} onClick={approveCharter}>
        Approve charter
      </button>
    </>
  ) : charter && !locked ? (
    <>
      {downloadWordButton}
      <button type="button" className="btn-ghost" style={{ color: '#A32D2D' }} disabled={saving || rexApplying} onClick={() => setShowResetModal(true)}>
        Reset charter
      </button>
      {rexEditedByRex && (
        <button type="button" className="btn-ghost" disabled={saving || rexApplying} onClick={requestOpenPanel}>
          Ask Rex to adjust
        </button>
      )}
      {charter.status === 'draft' && (
        <button type="button" className="btn-primary" disabled={saving || rexApplying} onClick={submitForReview}>
          Submit for review
        </button>
      )}
    </>
  ) : charter ? (
    downloadWordButton
  ) : null;

  const orderedSections = CHARTER_SECTION_ORDER.map((key) =>
    sections.find((s) => s.section_key === key)
  ).filter(Boolean);

  return (
    <InitiationLayout
      title={scopeTitle}
      subtitle={`${scopeEntityName} · IN-01`}
      badge={badge}
      actions={
        canWrite && !charter ? (
          <button type="button" className="btn-primary" disabled={saving} onClick={createCharter}>
            {saving ? 'Creating…' : 'Start charter'}
          </button>
        ) : actions
      }
    >
      {canSwitchScope && (
        <div className="charter-scope-tabs" role="tablist" aria-label="Charter scope">
          <button
            type="button"
            role="tab"
            aria-selected={charterScope === CHARTER_SCOPE.programme}
            className={`charter-scope-tab${charterScope === CHARTER_SCOPE.programme ? ' active' : ''}`}
            onClick={() => setCharterScope(CHARTER_SCOPE.programme)}
          >
            Programme charter
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={charterScope === CHARTER_SCOPE.project}
            className={`charter-scope-tab${charterScope === CHARTER_SCOPE.project ? ' active' : ''}`}
            onClick={() => setCharterScope(CHARTER_SCOPE.project)}
          >
            Project charter
          </button>
        </div>
      )}

      {!charter ? (
        <div className="card" style={{ padding: 24 }}>
          <p style={{ color: '#5f5e5a', fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
            {charterScope === CHARTER_SCOPE.project ? (
              <>
                Define project-specific scope, delivery boundaries, and success criteria for <strong>{project?.name}</strong>.
                The programme charter sets the overall envelope; this charter narrows scope to this project.
              </>
            ) : (
              <>
                Define programme scope, budget envelope, stakeholders, and success criteria for <strong>{programme.name}</strong>.
                The budget envelope anchors ROM estimates later in the initiation chain.
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="charter-body" ref={charterBodyRef}>
          {rexApplying && (
            <div className="rex-fill-banner loading">
              <span>Rex is drafting your charter…</span>
            </div>
          )}

          {showRexBanner && !rexApplying && (
            <div className="rex-fill-banner">
              <span>
                ✦ Rex has drafted this charter — review and edit freely, then submit for approval when ready.
              </span>
              <button type="button" className="rex-fill-dismiss" onClick={() => setShowRexBanner(false)}>
                Dismiss
              </button>
            </div>
          )}

          {status === 'review' && (
            <div className="submit-banner">
              <span className="submit-banner-icon">⏳</span>
              <div>
                Awaiting approval
                {charter.submitted_at && ` · submitted ${new Date(charter.submitted_at).toLocaleDateString()}`}
              </div>
            </div>
          )}
          {status === 'approved' && (
            <div className="approval-banner">
              <span className="approval-banner-icon">✓</span>
              <div className="approval-banner-text">
                <strong>Charter approved</strong>
                {charter.approved_at && ` · ${new Date(charter.approved_at).toLocaleDateString()}`}
                {' · '}Budget envelope locked for ROM comparison.
              </div>
            </div>
          )}

          <div className="card charter-header-card" style={{ padding: 14 }}>
            <div className="sec-hdr">
              <span className="sec-title">Charter header</span>
            </div>
            {!readOnly ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="form-row">
                  <label className="form-label">Title</label>
                  {rexDrafting ? (
                    <FieldSkeleton lines={1} />
                  ) : (
                    <CharterTextField
                      className={rexInputClass(rexFilledKeys, 'header.title')}
                      value={headerDraft.title}
                      onChange={(e) => scheduleHeaderSave({ title: e.target.value })}
                      onBlur={() => { flushHeaderSave(); clearRexField('header.title'); }}
                    />
                  )}
                </div>
                <div className="form-row">
                  <label className="form-label">Summary</label>
                  {rexDrafting ? (
                    <FieldSkeleton lines={3} />
                  ) : (
                    <AutoSizeTextarea
                      className={charterTextareaClass(rexInputClass(rexFilledKeys, 'header.summary', { multiline: true }))}
                      minRows={2}
                      placeholder="Not set"
                      value={headerDraft.summary}
                      onChange={(e) => scheduleHeaderSave({ summary: e.target.value })}
                      onBlur={() => { flushHeaderSave(); clearRexField('header.summary'); }}
                    />
                  )}
                </div>
                <div className="form-2col">
                  <div className="form-row">
                    <label className="form-label">Budget envelope</label>
                    {rexDrafting ? (
                      <FieldSkeleton lines={1} />
                    ) : (
                      <input
                        className={rexInputClass(rexFilledKeys, 'header.budget_envelope')}
                        type="number"
                        min="0"
                        step="1000"
                        placeholder="Not set"
                        value={headerDraft.budget_envelope}
                        onChange={(e) => scheduleHeaderSave({ budget_envelope: e.target.value })}
                        onBlur={() => { flushHeaderSave(); clearRexField('header.budget_envelope'); }}
                      />
                    )}
                  </div>
                  <div className="form-row">
                    <label className="form-label">Currency</label>
                    {rexDrafting ? (
                      <FieldSkeleton lines={1} />
                    ) : (
                      <select
                        className="form-select charter-inline-input"
                        value={headerDraft.budget_currency}
                        onChange={(e) => {
                          const next = { ...headerDraft, budget_currency: e.target.value };
                          setHeaderDraft(next);
                          saveHeader(next, { silent: true });
                        }}
                      >
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge variant="">{orderedSections.length} sections</Badge>
                </div>
              </div>
            ) : (
              <>
                {charter.summary && (
                  <p style={{ fontSize: 12, color: '#5f5e5a', lineHeight: 1.55, marginBottom: 10 }}>{charter.summary}</p>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {charter.budget_envelope != null && (
                    <Badge variant="pu">
                      Envelope: {charter.budget_currency || ''} {Number(charter.budget_envelope).toLocaleString()}
                    </Badge>
                  )}
                  <Badge variant="">{orderedSections.length} sections</Badge>
                </div>
              </>
            )}
          </div>

          {orderedSections.map((section) => (
            <CharterSectionCard
              key={section.id}
              section={section}
              readOnly={readOnly}
              saveStatus={sectionSaveStatus}
              rexFilledKeys={rexFilledKeys}
              onClearRexField={clearRexField}
              onSave={saveSection}
              showSkeleton={rexDrafting}
            />
          ))}
        </div>
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}

      {showResetModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-hdr">
              Reset {scopeTitle.toLowerCase()}?
              <button type="button" className="modal-close" onClick={() => setShowResetModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 12, color: '#5f5e5a', lineHeight: 1.55, margin: 0 }}>
                This deletes the charter and all sections from the database. You can start a fresh charter afterwards.
                This cannot be undone.
              </p>
            </div>
            <div className="modal-ftr">
              <button type="button" className="btn-ghost" disabled={saving} onClick={() => setShowResetModal(false)}>Cancel</button>
              <button type="button" className="btn-primary" style={{ background: '#A32D2D', borderColor: '#A32D2D' }} disabled={saving} onClick={resetCharter}>
                {saving ? 'Deleting…' : 'Reset charter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </InitiationLayout>
  );
}
